import { useId, type CSSProperties } from 'react';

import { unitMapping, type Unit } from '#src/lib/style.ts';

import {
  colorsByTime,
  timeKeyFromProgress,
  type TimeKey,
} from '../colorsByTime.ts';

// Face circle geometry (in 32x32 viewBox)
const CX = 18.5;
const CY = 18.5;
const R = 12;
const PR = R * 0.7;

const piePath = (percentage: number): string => {
  if (percentage <= 0) return '';
  if (percentage >= 1) {
    return `M ${CX} ${CY - PR} A ${PR} ${PR} 0 1 1 ${CX - 0.001} ${CY - PR} Z`;
  }
  const angle = percentage * 2 * Math.PI;
  const endX = (CX + PR * Math.sin(angle)).toFixed(4);
  const endY = (CY - PR * Math.cos(angle)).toFixed(4);
  const largeArc = percentage > 0.5 ? 1 : 0;
  return `M ${CX} ${CY} L ${CX} ${CY - PR} A ${PR} ${PR} 0 ${largeArc} 1 ${endX} ${endY} Z`;
};

type Props = {
  size?: Unit;
  timeProgress?: number | null;
  locked?: boolean;
  style?: CSSProperties;
};

export const StopwatchIcon = ({
  size = 2,
  timeProgress = 1,
  ...props
}: Props) => {
  const fillId = useId();
  const ringId = useId();

  const timeKey: TimeKey =
    timeProgress === null ? 'locked' : timeKeyFromProgress(timeProgress);

  const colors = colorsByTime[timeKey];
  const path = timeProgress == null ? '' : piePath(1 - timeProgress / 10000);
  const showRing =
    timeProgress === null || (timeProgress !== null && timeProgress <= 0);

  return (
    <svg
      viewBox="0 0 37 37"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        width: unitMapping[size],
        height: unitMapping[size],
        ...props.style,
      }}
    >
      <g filter="url(#filter0_i_1_2)">
        <path
          d="M22.9062 6.04617H13.9062C9.48797 6.04617 5.90625 9.62789 5.90625 14.0462V23.0462C5.90625 27.4644 9.48797 31.0462 13.9062 31.0462H22.9062C27.3245 31.0462 30.9062 27.4644 30.9062 23.0462V14.0462C30.9062 9.62789 27.3245 6.04617 22.9062 6.04617Z"
          fill="#0E0E0E"
        />
        <path
          d="M22.9062 6.54617H13.9062C9.76411 6.54617 6.40625 9.90403 6.40625 14.0462V23.0462C6.40625 27.1883 9.76411 30.5462 13.9062 30.5462H22.9062C27.0484 30.5462 30.4062 27.1883 30.4062 23.0462V14.0462C30.4062 9.90403 27.0484 6.54617 22.9062 6.54617Z"
          stroke="url(#paint0_linear_1_2)"
        />
      </g>
      <path
        d="M30.4227 8.48173L29.2902 9.81284L25.8048 7.00522L27.421 4.99936L30.1795 7.39248L30.4227 8.48173ZM22.1075 3.99031H19.5529V6.39682H17.2599V3.99031H14.7052V1.70151H21.2442L22.1075 2.30744V3.99031Z"
        fill="#9A9A9A"
      />
      {/* Pie fill */}
      {!!path && <path d={path} fill={`url(#${fillId})`} />}

      {/* Luminosity ring — only for empty/locked state */}
      {!!showRing && (
        <circle
          cx={CX}
          cy={CY}
          r={PR}
          stroke={`url(#${ringId})`}
          strokeOpacity={0.24}
          opacity={0.5}
          style={{ mixBlendMode: 'luminosity' }}
          {...(timeProgress === null && { fill: `url(#${fillId})` })}
        />
      )}
      <defs>
        <filter
          id="filter0_i_1_2"
          x="5.90625"
          y="6.04617"
          width="25"
          height="25"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="4.58816" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.290114 0 0 0 0 0.290114 0 0 0 0 0.290114 0 0 0 1 0"
          />
          <feBlend mode="normal" in2="shape" result="effect1_innerShadow_1_2" />
        </filter>
        <linearGradient
          id="paint0_linear_1_2"
          x1="20.1997"
          y1="31.0462"
          x2="20.1997"
          y2="10.1587"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#2F3435" />
          <stop offset="0.629808" stopColor="white" stopOpacity="0.17" />
          <stop offset="1" stopColor="white" />
        </linearGradient>
        <radialGradient
          id={fillId}
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(16 5) scale(22)"
        >
          <stop stopColor={colors.start} />
          <stop offset="1" stopColor={colors.end} />
        </radialGradient>
        {!!showRing && (
          <radialGradient
            id={ringId}
            cx="0"
            cy="0"
            r="1"
            gradientUnits="userSpaceOnUse"
            gradientTransform={`translate(${CX} ${CY + 3}) scale(8)`}
          >
            <stop stopColor="#FF2828" />
            <stop offset="1" stopColor="#FF5A5A" />
          </radialGradient>
        )}
      </defs>
    </svg>
  );
};
